<template>
  <div>
    <header-component />
    <div class="container-fluid mt--6">
      <div class="card">
        <div class="card-header">
          <h3 class="mb-0" style="text-transform: capitalize">
            Sale Quotations
          </h3>
        </div>

        <!-- Custom Tailwind CSS Table -->
        <div class="overflow-x-auto">
          <table class="min-w-full bg-white border border-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th
                  class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b"
                >
                  Sale Quotation No.
                </th>
                <th
                  class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b"
                >
                  Customer
                </th>
                <th
                  class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b"
                >
                  Type
                </th>
                <th
                  class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b"
                >
                  Date
                </th>
                <th
                  class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b"
                >
                  Total
                </th>
                <th
                  class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b"
                >
                  Status
                </th>
                <th
                  class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b"
                >
                  Actions
                </th>
              </tr>
              <!-- Filter Row -->
              <tr class="bg-gray-100">
                <th class="px-2 py-2 border-b">
                  <input
                    :key="'qn-' + filterResetKey"
                    v-model="columnFilters.quotationNumber"
                    type="text"
                    placeholder="Search..."
                    class="px-2 py-1 w-full text-xs rounded border border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    @input="onColumnFilterChange"
                  />
                </th>
                <th class="px-2 py-2 border-b">
                  <input
                    :key="'cn-' + filterResetKey"
                    v-model="columnFilters.customerName"
                    type="text"
                    placeholder="Search name..."
                    class="px-2 py-1 w-full text-xs rounded border border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    @input="onColumnFilterChange"
                  />
                </th>
                <th class="px-2 py-2 border-b">
                  <span class="block w-full"></span>
                </th>
                <th class="px-2 py-2 border-b">
                  <input
                    :key="'date-' + filterResetKey"
                    v-model="columnFilters.date"
                    type="date"
                    class="px-2 py-1 w-full text-xs rounded border border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    @input="onColumnFilterChange"
                  />
                </th>
                <th class="px-2 py-2 border-b">
                  <input
                    :key="'total-' + filterResetKey"
                    v-model="columnFilters.total"
                    type="number"
                    placeholder="Min amount..."
                    class="px-2 py-1 w-full text-xs rounded border border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    @input="onColumnFilterChange"
                  />
                </th>
                <th class="px-2 py-2 border-b">
                  <select
                    :key="'status-' + filterResetKey"
                    v-model="columnFilters.status"
                    class="px-2 py-1 w-full text-xs rounded border border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    @change="onColumnFilterChange"
                  >
                    <option value="">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="dispatched">Dispatched</option>
                    <option value="accepted">Accepted</option>
                    <option value="declined">Declined</option>
                  </select>
                </th>
                <th class="px-2 py-2 border-b">
                  <button
                    type="button"
                    :disabled="!hasActiveFilters"
                    @click="clearColumnFilters"
                    class="px-2 py-1 w-full text-xs text-white rounded focus:outline-none focus:ring-1 focus:ring-red-500"
                    :class="
                      hasActiveFilters
                        ? 'bg-red-500 hover:bg-red-600'
                        : 'bg-gray-300 cursor-not-allowed'
                    "
                  >
                    Clear
                  </button>
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr
                v-for="quotation in userQuotations"
                :key="
                  quotation.userQuotationId || quotation.serviceRequestQuotationId
                "
                class="hover:bg-gray-50"
              >
                <td
                  class="px-4 py-3 text-sm text-gray-900 border-b"
                >
                  <div class="flex flex-col gap-1">
                    <span>{{ quotation.quotationNumber || "-" }}</span>
                    <span
                      v-if="quotation.serviceRequestNumber"
                      class="text-xs text-gray-500"
                    >
                      SR No: {{ quotation.serviceRequestNumber }}
                    </span>
                  </div>
                </td>
                <td
                  class="px-4 py-3 whitespace-nowrap text-sm text-gray-900 border-b"
                >
                  {{ quotation.user.firstName + " " + quotation.user.lastName }}
                </td>
                <td
                  class="px-4 py-3 text-sm text-gray-900 border-b"
                >
                  <span
                    v-if="quotation.quotationType"
                    class="inline-flex items-center justify-center px-2 py-1 text-xs font-semibold text-center rounded-full"
                    :class="
                      isDetailedProductQuotation(quotation)
                        ? 'bg-blue-100 text-blue-800 min-w-[230px]'
                        : 'bg-gray-100 text-gray-700 min-w-[160px]'
                    "
                  >
                    {{ quotation.quotationType }}
                  </span>
                </td>
                <td
                  class="px-4 py-3 whitespace-nowrap text-sm text-gray-900 border-b"
                >
                  {{ formatDate(quotation.createdAt) }}
                </td>
                <td
                  class="px-4 py-3 whitespace-nowrap text-sm text-gray-900 border-b"
                >
                  ₹{{ quotation.total }}
                </td>
                <td class="px-4 py-3 whitespace-nowrap text-sm border-b">
                  <span
                    v-if="quotation.status"
                    class="inline-flex px-2 py-1 text-xs font-semibold rounded-full"
                    :class="{
                      'bg-red-100 text-red-800':
                        quotation.status.code === 'pending',
                      'bg-orange-100 text-orange-800':
                        quotation.status.code === 'dispatched',
                      'bg-green-100 text-green-800':
                        quotation.status.code === 'accepted',
                      'bg-gray-100 text-gray-800':
                        quotation.status.code === 'declined'
                    }"
                  >
                    {{ quotation.status.label }}
                  </span>
                </td>
                <td
                  class="px-4 py-3 whitespace-nowrap text-sm text-gray-900 border-b"
                >
                  <div class="relative inline-block text-left">
                    <button
                      @click="
                        toggleDropdown(
                          quotation.userQuotationId ||
                            quotation.serviceRequestQuotationId
                        )
                      "
                      class="inline-flex justify-center w-full px-3 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Actions
                      <svg
                        class="w-4 h-4 ml-2 -mr-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clip-rule="evenodd"
                        />
                      </svg>
                    </button>

                    <!-- Dropdown Menu -->
                    <div
                      v-if="
                        activeDropdown ===
                          (quotation.userQuotationId ||
                            quotation.serviceRequestQuotationId)
                      "
                      class="absolute right-0 z-10 w-48 mt-2 origin-top-right bg-white border border-gray-200 rounded-md shadow-lg ring-1 ring-black ring-opacity-5"
                    >
                      <div class="py-1">
                        <button
                          @click="handleAction(quotation, 'edit')"
                          class="block w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100"
                        >
                          Edit
                        </button>
                        <button
                          @click="handleAction(quotation, 'whatsApp')"
                          class="block w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100"
                        >
                          Send To Whatsapp
                        </button>
                        <button
                          @click="handleAction(quotation, 'print')"
                          class="block w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100"
                        >
                          Print
                        </button>
                        <button
                          @click="handleAction(quotation, 'convertSale')"
                          class="block w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100"
                        >
                          Convert To Sale
                        </button>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div class="card-footer d-flex justify-content-center py-3">
          <el-pagination
            @current-change="handlePageChange"
            :current-page.sync="currentPage"
            :page-size="perPage"
            :total="totalItems"
            layout="prev, pager, next"
            background
          >
          </el-pagination>
        </div>
      </div>
    </div>

    <!-- Dialogs -->
    <el-dialog
      :visible.sync="showSaleQuotationModal"
      title="Sale Quotation Invoice"
      width="70%"
      top="5vh"
      custom-class="sale-quotation-invoice-dialog"
    >
      <component
        :is="quotationModalComponent"
        :data="quotationData"
        @close="showSaleQuotationModal = false"
      />
    </el-dialog>
    <el-dialog
      :visible.sync="isSaleConfirmationModal"
      title="Quotation to Sale "
      width="30%"
    >
      <div>
        <p class="py-4">
          Are you sure, do you want to covert quotation for customer
          <span
            class="capitalize"
            v-if="this.quotationData && this.quotationData.user"
          >
            {{
              this.quotationData.user.firstName +
                " " +
                this.quotationData.user.lastName
            }}
          </span>
          to sale ?
        </p>
        <div class="flex gap-4">
          <button
            @click="handleConvertQuotationToSale()"
            class="px-6 py-2 text-white bg-blue-500 rounded hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Yes
          </button>
          <button
            @click="isSaleConfirmationModal = false"
            class="px-6 py-2 text-white bg-blue-500 rounded hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            No
          </button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>
<script>
import headerComponent from "../Components/HeaderComponent";
import { mapActions, mapState } from "vuex";
import dateFns from "date-fns";
import { print } from "@/utils/print";
import SaleQuotationDesign from "./SaleQuotationDesign.vue";
import ServiceRequestQuotationDesign from "../ServiceRequestQuotation/ServiceRequestQuotationDesign.vue";

import {
  Select,
  Option,
  DatePicker,
  DropdownMenu,
  DropdownItem,
  Dropdown,
  Dialog,
  Button,
  Tag,
  Pagination
} from "element-ui";
import { mapGetters, mapMutations } from "vuex/dist/vuex.common.js";
export default {
  components: {
    [Tag.name]: Tag,
    [Pagination.name]: Pagination,
    "header-component": headerComponent,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem,
    [Select.name]: Select,
    [Option.name]: Option,
    [DatePicker.name]: DatePicker,
    [Dialog.name]: Dialog,
    [Button.name]: Button,
    "sale-quotation-design": SaleQuotationDesign,
    "service-request-quotation-design": ServiceRequestQuotationDesign
  },

  data: () => {
    return {
      showSaleQuotationModal: false,
      isSaleConfirmationModal: false,
      quotationData: {},
      quotationModalComponent: "sale-quotation-design",
      currentPage: 1,
      perPage: 10,
      activeDropdown: null,
      filterTimeout: null,
      filterResetKey: 0,
      columnFilters: {
        quotationNumber: "",
        customerName: "",
        date: "",
        total: "",
        status: ""
      }
    };
  },
  computed: {
    ...mapState("user", ["userQuotations", "userQuotationMeta"]),
    ...mapGetters({
      loggedInUser: "user/getLoggedInUser",
      paymentModes: "paymentMode/getPaymentModes",
      statuses: "status/getStatusList"
    }),
    totalItems() {
      return this.userQuotationMeta.total || 0;
    },
    hasActiveFilters() {
      return !!(
        this.columnFilters.quotationNumber ||
        this.columnFilters.customerName ||
        this.columnFilters.date ||
        this.columnFilters.total ||
        this.columnFilters.status
      );
    }
  },
  beforeMount() {
    this.getPaymentModes();
    this.getStatuses();
    this.applyColumnFilters();
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleClickOutside);
    clearTimeout(this.filterTimeout);
  },
  methods: {
    ...mapActions("user", [
      "getUserQuotations",
      "getUserQuotation",
      "printQuotation"
    ]),
    ...mapActions("serviceRequestQuotation", [
      "getServiceRequestQuotationDetails",
      "updateServiceRequestQuotation"
    ]),
    handleClickOutside(event) {
      if (!event.target.closest(".relative")) {
        this.activeDropdown = null;
      }
    },
    ...mapActions("sale", ["createSale"]),
    ...mapActions({
      getPaymentModes: "paymentMode/getPaymentModes",
      getStatuses: "status/getStatusList",
      updateUserQuotation: "user/updateUserQuotation"
    }),
    ...mapMutations("sale", ["setSale"]),
    formatDate(value) {
      return dateFns.format(new Date(value), "YYYY-MM-DD");
    },
    isDetailedProductQuotation(quotation) {
      return quotation.quotationSource === "service_request_detailed_product";
    },
    buildFilterParams() {
      const params = {};
      if (this.columnFilters.quotationNumber) {
        params.quotationNumber = this.columnFilters.quotationNumber;
      }
      if (this.columnFilters.customerName) {
        params.customerName = this.columnFilters.customerName;
      }
      if (this.columnFilters.date) {
        params.fromDate = this.columnFilters.date;
        params.toDate = this.columnFilters.date;
      }
      if (this.columnFilters.total) {
        params.total = this.columnFilters.total;
      }
      if (this.columnFilters.status) {
        params.status = this.columnFilters.status;
      }
      return params;
    },
    onColumnFilterChange() {
      clearTimeout(this.filterTimeout);
      this.filterTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.applyColumnFilters();
      }, 300);
    },
    applyColumnFilters() {
      this.getUserQuotations({
        page: this.currentPage,
        perPage: this.perPage,
        ...this.buildFilterParams()
      });
    },
    clearColumnFilters() {
      clearTimeout(this.filterTimeout);
      this.filterTimeout = null;
      this.columnFilters.quotationNumber = "";
      this.columnFilters.customerName = "";
      this.columnFilters.date = "";
      this.columnFilters.total = "";
      this.columnFilters.status = "";
      this.filterResetKey += 1;
      this.currentPage = 1;
      this.applyColumnFilters();
    },
    handlePageChange(page) {
      this.currentPage = page;
      this.applyColumnFilters();
    },
    async handleAction(userQuotation, command) {
      if (this.isDetailedProductQuotation(userQuotation)) {
        if (command === "edit") {
          this.$router.push("/quotation/" + userQuotation.serviceRequestQuotationId);
        }
        if (command === "print" || command === "whatsApp") {
          const quotationData = await this.getServiceRequestQuotationDetails(
            userQuotation.serviceRequestQuotationId
          );
          this.quotationData = quotationData;
          this.quotationModalComponent = "service-request-quotation-design";
          this.showSaleQuotationModal = true;
        }
        if (command === "convertSale") {
          this.quotationData = userQuotation;
          this.isSaleConfirmationModal = true;
        }
        return;
      }
      if (command === "edit") {
        this.$router.push("/sales/quotations/" + userQuotation.userQuotationId);
      }
      if (command === "print") {
        const quotationData = await this.getUserQuotation(
          userQuotation.userQuotationId
        );
        this.quotationData = quotationData;
        this.quotationModalComponent = "sale-quotation-design";
        print(
          { ...quotationData, quotationUser: quotationData.user },
          "sale-quotation"
        );
      }
      if (command === "whatsApp") {
        const quotationData = await this.getUserQuotation(
          userQuotation.userQuotationId
        );
        this.quotationData = quotationData;
        this.quotationModalComponent = "sale-quotation-design";
        this.showSaleQuotationModal = true;
      }
      if (command === "convertSale") {
        this.quotationData = userQuotation;
        this.isSaleConfirmationModal = true;
      }
    },
    toggleDropdown(quotationId) {
      this.activeDropdown =
        this.activeDropdown === quotationId ? null : quotationId;
    },
    async handleConvertQuotationToSale() {
      const isDetailedProduct = this.isDetailedProductQuotation(this.quotationData);
      const saleData = {
        userId: this.quotationData.userId,
        products: this.quotationData.productMeta.map(i => {
          return {
            productId: i.productId,
            name: i.name,
            price: i.price,
            quantity: i.quantity,
            discount: i.discount,
            isPercentage: i.isPercentage,
            productPriceTypeId: i.productPriceTypeId,
            taxRate: i.taxRate
          };
        }),
        date: this.quotationData.createdAt,
        createdBy:
          this.quotationData.createdBy ||
          (this.loggedInUser && this.loggedInUser.userId),
        discount: 0,
        paymentModeId: this.paymentModes[0].paymentModeId,
        paymentNote: "",
        amountPaid: 0
      };
      await this.setSale(saleData);
      this.createSale(saleData)
        .then(() => {
          const acceptedStatus = this.statuses.find(i => i.code === "accepted");
          const updatePromise = isDetailedProduct
            ? this.updateServiceRequestQuotation({
                serviceRequestQuotationId:
                  this.quotationData.serviceRequestQuotationId,
                statusId: acceptedStatus.statusId,
                serviceRequestId: this.quotationData.serviceRequestId,
                productMeta: this.quotationData.productMeta,
                total: this.quotationData.total,
                approvedBy:
                  this.loggedInUser && this.loggedInUser.userId
                    ? this.loggedInUser.userId
                    : undefined
              })
            : this.updateUserQuotation({
                ...this.quotationData,
                statusId: acceptedStatus.statusId
              });
          updatePromise.then(() => {
            this.applyColumnFilters();
          });
          this.$notify({
            message: "Sale Saved Succesfully!",
            type: "success",
            title: "Success"
          });
          this.isSaleConfirmationModal = false;
        })
        .catch(err => {
          console.log("err", err);
        });
    }
  }
};
</script>
<style>
.dispatch-button {
  margin-left: 15px;
  margin-top: 20px;
}
.pending-status {
  background-color: #e72626;
  border-color: #e72626;
  color: #fff;
}
.dispatch-status {
  background-color: #f57316eb;
  border-color: #f57316eb;
  color: #fff;
}
.declined-status {
  background-color: #b0ccb3;
  border-color: #b0ccb3;
  color: #fff;
}
.sale-quotation-invoice-dialog {
  max-width: 960px;
}
.sale-quotation-invoice-dialog .el-dialog__body {
  overflow-x: hidden;
}
</style>
